import { Building2, Building, Eye, Edit3, Trash2, MapPin, Phone, Calendar } from 'lucide-react'

interface Construtora {
    id: string
    nome: string
    cnpj: string
    contato: string | null
    estado: string | null
    cidade: string | null
    created_at: string
    imoveis_count?: number
    valor_total_imoveis?: number
}

interface ConstrutorasCardViewProps {
    construtoras: Construtora[]
    onView: (construtora: Construtora) => void
    onEdit: (construtora: Construtora) => void
    onDelete: (id: string, nome: string) => void
    formatDate: (date: string) => string
}

export default function ConstrutorasCardView({
    construtoras,
    onView,
    onEdit,
    onDelete,
    formatDate
}: ConstrutorasCardViewProps) {
    if (construtoras.length === 0) {
        return (
            <div className="text-center py-12">
                <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Nenhuma construtora encontrada</h3>
                <p className="text-gray-500 dark:text-gray-400">
                    Comece criando sua primeira construtora pessoal
                </p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                    💡 Você só vê construtoras que você cadastrou
                </p>
            </div>
        )
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {construtoras.map((construtora) => (
                <div
                    key={construtora.id}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow duration-200"
                >
                    {/* Header do Card */}
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3 flex-1">
                            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                                    {construtora.nome}
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-1">
                                    {construtora.cnpj}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Informações do Card */}
                    <div className="space-y-3 mb-4">
                        {/* Contato */}
                        {construtora.contato && (
                            <div className="flex items-start gap-2">
                                <Phone className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                                    {construtora.contato}
                                </p>
                            </div>
                        )}

                        {/* Localização */}
                        {(construtora.estado || construtora.cidade) && (
                            <div className="flex items-start gap-2">
                                <MapPin className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                    {construtora.cidade && construtora.estado
                                        ? `${construtora.cidade}, ${construtora.estado}`
                                        : construtora.cidade || construtora.estado || '-'}
                                </p>
                            </div>
                        )}

                        {/* Data de Cadastro */}
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Cadastrado em {formatDate(construtora.created_at)}
                            </p>
                        </div>
                    </div>

                    {/* Estatísticas */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mb-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Building className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                <span className="text-sm text-gray-600 dark:text-gray-400">Imóveis</span>
                            </div>
                            <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                {construtora.imoveis_count || 0}
                            </span>
                        </div>
                    </div>

                    {/* Ações */}
                    <div className="flex items-center gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                            onClick={() => onView(construtora)}
                            className="flex-1 px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        >
                            <Eye className="w-4 h-4 inline mr-1" />
                            Ver
                        </button>
                        <button
                            onClick={() => onEdit(construtora)}
                            className="flex-1 px-3 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                        >
                            <Edit3 className="w-4 h-4 inline mr-1" />
                            Editar
                        </button>
                        <button
                            onClick={() => onDelete(construtora.id, construtora.nome)}
                            className="px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            ))}
        </div>
    )
}

